import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Timer = ({ expiresAt, serverTime, isPaused }) => {
  const initialRemaining = Math.max(0, Math.floor((expiresAt - serverTime) / 1000));
  const [timeLeft, setTimeLeft] = useState(initialRemaining);

  useEffect(() => {
    if (isPaused) return;
    const localStartTime = Date.now();
    const interval = setInterval(() => {
      const elapsedLocal = Date.now() - localStartTime;
      const remaining = Math.max(0, Math.floor((expiresAt - serverTime - elapsedLocal) / 1000));
      setTimeLeft(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [expiresAt, serverTime, isPaused]);

  if (isPaused) {
    return (
      <div style={{
        background: 'rgba(15, 23, 42, 0.85)',
        border: '2px solid #3b82f6',
        boxShadow: '0 4px 8px rgba(0,0,0,0.5)',
        padding: '0 10px',
        height: '26px',
        borderRadius: '13px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.75rem',
        fontWeight: '900',
        color: '#60a5fa',
        textShadow: '0 1px 2px rgba(0,0,0,0.5)',
        letterSpacing: '0.5px'
      }}>
        ⏸ Paused
      </div>
    );
  }

  if (timeLeft <= 0) return null;

  return (
    <div style={{
      background: 'rgba(15, 23, 42, 0.85)',
      border: `2px solid ${timeLeft <= 5 ? '#b91c1c' : '#b45309'}`,
      boxShadow: `0 4px 8px rgba(0,0,0,0.5)${timeLeft <= 5 ? ', 0 0 8px rgba(239,68,68,0.5)' : ''}`,
      padding: '0 10px',
      height: '26px',
      borderRadius: '13px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.8rem',
      fontWeight: '900',
      color: timeLeft <= 5 ? '#ef4444' : '#fbbf24',
      textShadow: '0 1px 2px rgba(0,0,0,0.5)',
      letterSpacing: '0.5px'
    }}>
      ⏱ {timeLeft}s
    </div>
  );
};

export default function PlayerLabel({ player, playerId, gs, myId, send, isSpectator }) {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);
  const avatarRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // If we clicked the avatar trigger, let the onClick handle it
      if (avatarRef.current && avatarRef.current.contains(event.target)) return;
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  // 1. THE PHASE CHECK
  const isGameActive = !gs?.phase || gs?.phase === 'waiting' || gs?.phase === 'bidding' || gs?.phase === 'trump_selection' || gs?.phase === 'playing' || gs?.phase === 'doubling' || gs?.phase === 'redoubling' || gs?.phase === 'round_over' || gs?.phase === 'match_over';

  // 2. EMPTY SEAT STATE (Add Bot or Take Over)
  if (!player) return (
    <div className="player-label disconnected" style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      height: '75px',
      minWidth: '220px' // Added to match filled state layout
    }}>
      <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem', marginBottom: '4px' }}>Empty seat</span>
      {isGameActive && (
        <button 
          onClick={(e) => {
            e.stopPropagation();
            if (isSpectator) {
              send('claim_seat', { target_slot: playerId });
            } else {
              send('add_bot', { target_id: playerId, difficulty: 'easy' });
            }
          }}
          style={{
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: 'white',
            borderRadius: '4px',
            fontSize: '0.65rem',
            padding: '4px 8px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
          onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'}
        >
          {isSpectator ? 'Take Over' : '+ Add Bot'}
        </button>
      )}
    </div>
  );
  
  const isMe = playerId === myId;
  const isActiveTurn = gs.phase === 'playing' && gs.current_turn === playerId;
  const isBidTurn = gs.phase === 'bidding' && gs.bid_turn === playerId;
  const isTrumpTurn = gs.phase === 'trump_selection' && gs.bid_winner === playerId;
  const isActingTurn = isActiveTurn || isBidTurn || isTrumpTurn;
  const isDealer = gs.dealer === playerId;
  const hasPassed = gs.passed_since_bid?.[playerId];
  const isForced = gs.forced_bidder === playerId;
  
  const isOnline = player.is_online !== false;
  const isBot = player.is_bot === true;
  const isPoker = gs?.mode === 'poker';
  const team = isPoker ? -1 : (playerId % 2);

  // Team Colors: Neutral for Poker, Red/Blue for others
  const teamColor = isPoker ? '#fbbf24' : (team === 0 ? '#f87171' : '#60a5fa'); 
  const accentColor = isPoker ? '#fbbf24' : (team === 0 ? '#ef4444' : '#3b82f6');

  const isMalayalam = player.avatar.startsWith('Mal_');
  const avatarSize = isMalayalam ? '95px' : '85px';
  const avatarMargin = isMalayalam ? '-35px' : '-30px';
  const containerHeight = isMalayalam ? '90px' : '85px';

  // 3. TAUNT OPTIONS DEFINITION - Fanning Down-Right
  const tauntOptions = [
    { item: 'tomato', icon: '🍅', title: 'Throw Tomato', x: 120, y: 0 },
    { item: 'egg', icon: '🥚', title: 'Throw Egg', x: 110, y: 46 },
    { item: 'coconut', icon: '🥥', title: 'Throw Coconut', x: 85, y: 85 },
    { item: 'brick', icon: '🧱', title: 'Throw Brick', x: 46, y: 110 },
    { item: 'poop', icon: '💩', title: 'Throw Poop', x: 0, y: 120 },
  ];

  // 4. EMOJI GRID DEFINITION (3x3)
  const emojiOptions = [
    { item: 'laughing', icon: '/assets/emojis/laughing.webp' },
    { item: 'happy-cry', icon: '/assets/emojis/happy-cry.webp' },
    { item: 'rolf', icon: '/assets/emojis/rolf.webp' },
    { item: 'sunglass-face', icon: '/assets/emojis/sunglass-face.webp' },
    { item: 'squinting-tongue', icon: '/assets/emojis/squinting-%20tongue.webp' },
    { item: 'fire', icon: '/assets/emojis/fire.webp' },
    { item: 'clown', icon: '/assets/emojis/clown.webp' },
    { item: 'cursing', icon: '/assets/emojis/cursing.webp' },
    { item: 'middle-finger', icon: '/assets/emojis/middle-finger.webp' },
  ];

  return (
    <div 
      className={`player-label-wrapper ${isActingTurn ? 'active-turn' : ''} ${isPoker ? 'poker-theme' : ''}`}
      style={{ 
        position: 'relative', 
        display: 'flex', 
        alignItems: 'flex-end', 
        overflow: 'visible',
        minWidth: '220px',
        height: containerHeight 
      }}
    >
      {/* 1. SIDE AVATAR CONTAINER */}
      <div style={{ position: 'relative', zIndex: 20 }}>
        <motion.div 
          ref={avatarRef}
          onClick={(e) => {
            if (isGameActive && !isSpectator) {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }
          }}
          whileHover={isGameActive && !isSpectator ? { scale: 1.05 } : {}}
          data-player-id={player?.id || playerId}
          style={{ 
            position: 'relative', 
            top: '12px', 
            marginRight: avatarMargin, 
            flexShrink: 0,
            cursor: isGameActive && !isSpectator ? 'pointer' : 'default',
            zIndex: 1
          }}
        >
          <img 
            src={isMalayalam ? `/assets/avatars/malayalam/${player.avatar}.webp` : `/assets/avatars/default/${player.avatar}.webp`} 
            alt={`${player.name}'s avatar`}
            style={{
              width: avatarSize,
              height: avatarSize,
              objectFit: 'contain',
              opacity: isOnline ? 1 : 0.5,
              transition: 'all 0.3s ease',
              filter: isActingTurn 
                ? `drop-shadow(0 0 12px ${accentColor})` 
                : 'drop-shadow(2px 4px 6px rgba(0,0,0,0.6))',
              transform: isActingTurn ? 'scale(1.1) translateY(-5px)' : 'scale(1)'
            }}
          />

          {/* INTERACTIVE HINT: Action Ellipsis for other players */}
          {isGameActive && !isMe && !isSpectator && (
            <div
              style={{
                position: 'absolute',
                bottom: '5px',
                right: '5px',
                width: '24px',
                height: '24px',
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                borderRadius: '50%',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '0.8rem',
                fontWeight: 'bold',
                pointerEvents: 'none'
              }}
            >
              ...
            </div>
          )}

          {/* INTERACTIVE HINT: Speech Bubble for yourself */}
          {isGameActive && isMe && !isSpectator && (
            <div
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              style={{
                position: 'absolute',
                bottom: '5px',
                right: '5px',
                width: '24px',
                height: '24px',
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                borderRadius: '50%',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '0.8rem',
                cursor: 'pointer',
                zIndex: 10
              }}
            >
              💬
            </div>
          )}
        </motion.div>

        {/* COMMUNICATION MENUS (Outside the avatar gesture area) */}
        <AnimatePresence>
          {showMenu && !isSpectator && (
            <>
              {/* TAUNT RADIAL MENU (For Opponents) */}
              {!isMe && (
                <div
                  ref={menuRef}
                  style={{
                    position: 'absolute',
                    top: '55px', 
                    left: '42px',
                    zIndex: 100,
                    pointerEvents: 'none'
                  }}
                >
                  {tauntOptions.map((opt, i) => (
                    <motion.button
                      key={opt.item}
                      initial={{ scale: 0, opacity: 0, x: 0, y: 0 }}
                      animate={{ scale: 1, opacity: 1, x: opt.x, y: opt.y }}
                      exit={{ scale: 0, opacity: 0, x: 0, y: 0 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 20, delay: i * 0.05 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        send('send_prank', { target_id: player.id, item: opt.item });
                        setShowMenu(false);
                      }}
                      style={{
                        position: 'absolute',
                        width: '44px', height: '44px', borderRadius: '50%',
                        backgroundColor: 'rgba(15, 23, 42, 0.95)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        backdropFilter: 'blur(8px)',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.6)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '1.8rem', cursor: 'pointer', pointerEvents: 'auto',
                        padding: 0, outline: 'none', marginLeft: '-22px', marginTop: '-22px'
                      }}
                      whileHover={{ scale: 1.1, backgroundColor: 'rgba(30, 41, 59, 0.95)' }}
                      whileTap={{ scale: 0.9 }}
                      title={opt.title}
                    >
                      {opt.icon}
                    </motion.button>
                  ))}
                </div>
              )}

              {/* EMOJI GRID MENU (For Yourself) */}
              {isMe && (
                <motion.div
                  ref={menuRef}
                  initial={{ scale: 0, opacity: 0, y: 20 }}
                  animate={{ scale: 1, opacity: 1, y: 0 }}
                  exit={{ scale: 0, opacity: 0, y: 20 }}
                  style={{
                    position: 'absolute',
                    bottom: '80px',
                    left: '10px',
                    zIndex: 100,
                    backgroundColor: 'rgba(15, 23, 42, 0.95)',
                    padding: '12px',
                    borderRadius: '16px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '10px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(12px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.8)',
                    pointerEvents: 'auto'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {emojiOptions.map((emoji, i) => (
                    <motion.button
                      key={i}
                      whileHover={{ scale: 1.1, backgroundColor: 'rgba(255,255,255,0.1)' }}
                      whileTap={{ scale: 0.9 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        // Use original item name for the message
                        const itemName = emoji.item === 'squinting-tongue' ? 'squinting- tongue' : emoji.item;
                        send('send_prank', { target_id: player.id, item: `emoji:${itemName}` });
                        setShowMenu(false);
                      }}
                      style={{
                        width: '40px',
                        height: '40px',
                        background: 'none',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        padding: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <img src={emoji.icon} alt="emoji" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </motion.button>
                  ))}
                </motion.div>
              )}
            </>
          )}
        </AnimatePresence>
      </div>

      {/* 2. CENTRALISED NAMEPLATE */}
      <div 
        className={`player-label ${isMe ? 'is-me' : ''}`}
        style={{
          flexGrow: 1,
          zIndex: 10,
          position: 'relative',
          borderRadius: '8px',
          backgroundColor: 'rgba(15, 23, 42, 0.96)',
          border: `1px solid ${isActingTurn ? accentColor : 'rgba(255,255,255,0.1)'}`,
          boxShadow: '0 6px 12px rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center', // Vertical Center
          alignItems: 'center',     // Horizontal Center
          height: '38px',           // Slimmed height
          backdropFilter: 'blur(12px)',
          paddingLeft: '20px'       // Offset for the avatar visual weight
        }}
      >
        <span style={{ 
          fontWeight: '900',
          color: isOnline ? teamColor : 'rgba(148, 163, 184, 0.4)', 
          fontSize: '1rem',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '140px',
          textAlign: 'center',
          textShadow: '0 2px 4px rgba(0,0,0,0.6)',
          letterSpacing: '0.5px'
        }}>
          {player.name}{isMe ? ' (You)' : ''}
        </span>

        {/* 3. COIN-STYLE TOP-RIGHT TOKENS */}
        <div style={{
          position: 'absolute',
          top: '-25px', 
          right: '4px',
          display: 'flex',
          gap: '6px',
          zIndex: 30
        }}>
          {/* TIMER TOKEN (Replaces inline timer) */}
          {isActingTurn && (gs.mode === '28' || gs.mode === '56') && gs.expires_at && gs.server_time && (
            <Timer expiresAt={gs.expires_at} serverTime={gs.server_time} isPaused={gs.is_paused} />
          )}
          {/* DEALER TOKEN: Gold Style */}
          {isDealer && (
            <div style={{
              background: 'linear-gradient(135deg, #fde047 0%, #ca8a04 100%)',
              color: '#451a03',
              width: '26px',
              height: '26px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.85rem',
              fontWeight: '900',
              border: '2px solid #78350f',
              boxShadow: '0 4px 8px rgba(0,0,0,0.5)',
              textShadow: '0 1px 0 rgba(255,255,255,0.3)'
            }}>
              D
            </div>
          )}
          

        </div>

        {/* 4. UNDER-LABEL STATUS */}
        <div style={{ 
          position: 'absolute', 
          bottom: '-18px', 
          display: 'flex', 
          gap: '6px', 
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          
          {/* BOT / OFFLINE TAKE OVER BUTTON */}
          {isSpectator && (isBot || !isOnline) && (
             <button 
              onClick={(e) => {
                e.stopPropagation();
                send('claim_seat', { target_slot: playerId });
              }}
              style={{
                background: 'rgba(16, 185, 129, 0.2)',
                border: '1px solid #10b981',
                color: '#34d399',
                borderRadius: '4px',
                fontSize: '0.6rem',
                padding: '1px 6px',
                cursor: 'pointer',
                fontWeight: 'bold',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.target.style.background = 'rgba(16, 185, 129, 0.4)'}
              onMouseOut={(e) => e.target.style.background = 'rgba(16, 185, 129, 0.2)'}
            >
              Take Over
            </button>
          )}

          {/* OFFLINE STATE (Replace with Bot) */}
          {!isOnline && (
            <>
              <span style={{ fontSize: '0.6rem', color: '#fca5a5', fontWeight: 'bold' }}>● OFFLINE</span>
              {isGameActive && (
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    send('replace_with_bot', { target_id: playerId });
                  }}
                  style={{
                    background: 'rgba(59, 130, 246, 0.2)',
                    border: '1px solid #3b82f6',
                    color: '#60a5fa',
                    borderRadius: '4px',
                    fontSize: '0.6rem',
                    padding: '1px 6px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.background = 'rgba(59, 130, 246, 0.4)'}
                  onMouseOut={(e) => e.target.style.background = 'rgba(59, 130, 246, 0.2)'}
                >
                  Replace w/ Bot
                </button>
              )}
            </>
          )}
          
          {(isForced || hasPassed) && gs.phase === 'bidding' && (
            <span style={{
              backgroundColor: isForced ? 'rgba(251, 191, 36, 0.2)' : 'rgba(239, 68, 68, 0.2)',
              color: isForced ? '#fbbf24' : '#f87171',
              fontSize: '0.6rem',
              fontWeight: 'bold',
              padding: '1px 8px',
              borderRadius: '20px',
              border: `1px solid ${isForced ? '#fbbf24' : '#ef4444'}`
            }}>
              {isForced ? 'FORCED' : 'PASS'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}